import * as api from "@/api";
import LocalStorage from "@/classes/local-storage";
import * as signals from "@/signals";
import {
	BingWallpaperResponse,
	BookmarkInfo,
	BookmarkResponse,
	FolderInfo,
	NoteInfo,
	NoteResponse,
	SearchEngineInfo,
} from "@/types";
import { deepCopy, isFolder, showDialog } from "@/utils";
import { arrayMove } from "@dnd-kit/sortable";
import { QueryClient } from "@tanstack/react-query";

class QueryDataManager {
	private readonly queryClient: QueryClient;

	public constructor(queryClient: QueryClient) {
		this.queryClient = queryClient;
	}

	public async addToDock(options: {
		id: string;
		type: string;
	}): Promise<void> {
		const { id, type } = options;
		const result = await api.addToDock({ id, type });
		if (result.alert) {
			void showDialog(result.alert);
		}
		if (!result.success) {
			return;
		}
		await this.invalidateBookmarks();
	}

	public async createBookmark(
		bookmark: BookmarkInfo,
		folderId?: string,
	): Promise<void> {
		const { id } = await api.createBookmark(bookmark, folderId);
		bookmark.id = id;
		await this.invalidateBookmarks();
	}

	public async createBookmarkFolder(folderName: string): Promise<void> {
		await api.createBookmarkFolder(folderName);
		await this.invalidateBookmarks();
	}

	public async createSearchEngine(
		name: string,
		url: string,
	): Promise<{
		id: string;
	}> {
		const { id } = await api.createSearchEngine(name, url);
		const newSearchEngine = {
			id: id,
			name: name,
			url: url,
		};
		this.queryClient.setQueryData<{
			results: SearchEngineInfo[];
		}>(["searchEngines", signals.login.value.username], (data) => {
			if (!data?.results) {
				return data;
			}
			data = deepCopy(data);
			data.results = [...data.results, newSearchEngine];
			LocalStorage.saveSearchEngines(data.results);
			return data;
		});
		return { id };
	}

	public async createNote(note: NoteInfo): Promise<{
		id: string;
	}> {
		const { id } = await api.createNote(note);
		const newNote = {
			...note,
			id: id,
		};
		this.queryClient.setQueryData<NoteResponse>(
			["notes", signals.login.value.username],
			(data) => {
				if (!data?.results) {
					return data;
				}
				data = deepCopy(data);
				data.results = [newNote, ...data.results];
				return data;
			},
		);
		return { id };
	}

	public async deleteBookmark(
		id: string,
		currentFolderId?: string,
	): Promise<void> {
		await api.deleteBookmark(id, currentFolderId);
		await this.invalidateBookmarks();
	}

	public async deleteBookmarkFolder(folderId: string): Promise<void> {
		await api.deleteBookmarkFolder(folderId);
		await this.invalidateBookmarks();
	}

	public async deleteNote(id: string): Promise<void> {
		await api.deleteNote(id);
		this.deleteNotesInQueryData((note) => note.id !== id);
	}

	public async deleteNotes(ids: Set<string>): Promise<void> {
		await api.deleteNotes([...ids]);
		this.deleteNotesInQueryData((note) => !ids.has(note.id));
	}

	private deleteNotesInQueryData(filter: (note: NoteInfo) => boolean): void {
		this.queryClient.setQueryData<NoteResponse>(
			["notes", signals.login.value.username],
			(data) => {
				if (!data?.results) {
					return data;
				}
				data = deepCopy(data);
				data.results = data.results.filter(filter);
				return data;
			},
		);
	}

	public async deleteSearchEngine(id: string): Promise<void> {
		await api.deleteSearchEngine(id);
		this.queryClient.setQueryData<{
			results: SearchEngineInfo[];
		}>(["searchEngines", signals.login.value.username], (data) => {
			if (!data?.results) {
				return data;
			}
			data = deepCopy(data);
			const newResults = data.results.filter(
				(searchEngine) => searchEngine.id !== id,
			);
			LocalStorage.saveSearchEngines(newResults);
			LocalStorage.resetSelectedSearchEngine();
			return {
				...data,
				results: newResults,
			};
		});
	}

	public async editBookmark(
		bookmark: BookmarkInfo,
		currentFolderId?: string,
		targetFolderId?: string,
	): Promise<void> {
		await api.editBookmark(bookmark, currentFolderId, targetFolderId);
		await this.invalidateBookmarks();
	}

	public async editNote(note: NoteInfo): Promise<void> {
		await api.editNote(note.id, note);
		this.updateNoteInQueryData(note);
	}

	private findFolder(
		data: BookmarkResponse,
		folderId?: string,
	): BookmarkResponse | FolderInfo {
		if (folderId) {
			for (const item of data.children) {
				if (isFolder(item) && item.id === folderId) {
					return item;
				}
			}
		}
		return data;
	}

	public async invalidateBookmarks(): Promise<void> {
		await this.queryClient.invalidateQueries({
			queryKey: ["bookmarks", signals.login.value.username],
		});
	}

	public async likeBingWallpaper(options: {
		bingWallpaperResponse?: BingWallpaperResponse;
	}): Promise<void> {
		const { bingWallpaperResponse } = options;
		const result = await api.likeBingWallpaper();
		if (result.alert) {
			void showDialog(result.alert);
		}
		if (
			!result.success ||
			!bingWallpaperResponse ||
			bingWallpaperResponse.likeCount === undefined
		) {
			return;
		}
		this.queryClient.setQueryData<BingWallpaperResponse>(
			["bingWallpaperInfo"],
			{
				...bingWallpaperResponse,
				likeCount: bingWallpaperResponse.likeCount + 1,
			},
		);
	}

	public async pinNote(note: NoteInfo): Promise<void> {
		await api.pinNote(note.id);
		const newNote: NoteInfo = {
			...note,
			isPinned: true,
			pinnedAt: new Date().toISOString(),
		};
		this.updateNoteInQueryData(newNote);
	}

	public async removeFromDock(id: string): Promise<void> {
		await api.removeFromDock(id);
		this.queryClient.setQueryData<BookmarkResponse>(
			["bookmarks", signals.login.value.username],
			(data) => {
				if (!data) {
					return data;
				}
				data = deepCopy(data);
				this.removeFromDockInData(data, id);
				return data;
			},
		);
	}

	private removeFromDockInData(data: BookmarkResponse, id: string): void {
		data.dockItems = data.dockItems.filter((item) => item.id !== id);
		LocalStorage.saveDockItems(data.dockItems);
	}

	public removePresetShortcut(id: string): void {
		LocalStorage.hidePresetShortcut(id);
		this.queryClient.setQueryData<BookmarkResponse>(
			["bookmarks", signals.login.value.username],
			(data) => {
				if (!data) {
					return data;
				}
				data = deepCopy(data);
				this.removeFromDockInData(data, id);
				data.children = data.children.filter((item) => item.id !== id);
				return data;
			},
		);
	}

	public async renameBookmarkFolder(
		folderId: string,
		newName: string,
	): Promise<void> {
		await api.renameBookmarkFolder(folderId, newName);
		this.queryClient.setQueryData<BookmarkResponse>(
			["bookmarks", signals.login.value.username],
			(data) => {
				if (!data) {
					return data;
				}
				data = deepCopy(data);
				const folderIndex = data.children.findIndex(
					(item) => item.id === folderId,
				);
				if (folderIndex === -1) {
					console.error("Folder not found in the list");
					return data;
				}
				data.children[folderIndex] = {
					...data.children[folderIndex],
					folderName: newName,
				};
				LocalStorage.saveShortcuts(data.children);
				return data;
			},
		);
	}

	public async reorderBookmark(id: string, newIndex: number): Promise<void> {
		this.queryClient.setQueryData<BookmarkResponse>(
			["bookmarks", signals.login.value.username],
			(data) => {
				if (!data) {
					return data;
				}
				data = deepCopy(data);
				const targetFolder = this.findFolder(
					data,
					signals.currentFolder.value?.id,
				);
				const oldIndex = targetFolder.children.findIndex(
					(item) => item.id === id,
				);
				targetFolder.children = arrayMove(
					targetFolder.children,
					oldIndex,
					newIndex,
				);
				LocalStorage.saveShortcuts(data.children);
				return data;
			},
		);
		await api.reorderBookmark(id, newIndex);
	}

	public async reorderDockItem(id: string, newIndex: number): Promise<void> {
		// -1 because the first item is allShortcuts
		this.queryClient.setQueryData<BookmarkResponse>(
			["bookmarks", signals.login.value.username],
			(data) => {
				if (!data) {
					return data;
				}
				data = deepCopy(data);
				const oldIndex = data.dockItems.findIndex(
					(item) => item.id === id,
				);
				data.dockItems = arrayMove(data.dockItems, oldIndex, newIndex);
				LocalStorage.saveDockItems(data.dockItems);
				return data;
			},
		);
		await api.reorderDockItem(id, newIndex);
	}

	public async unpinNote(note: NoteInfo): Promise<void> {
		await api.unpinNote(note.id);
		const newNote: NoteInfo = {
			...note,
			isPinned: false,
			pinnedAt: undefined,
		};
		this.updateNoteInQueryData(newNote);
	}

	private updateNoteInQueryData(note: NoteInfo): void {
		this.queryClient.setQueryData<NoteResponse>(
			["notes", signals.login.value.username],
			(data) => {
				if (!data?.results) {
					return data;
				}
				data = deepCopy(data);
				const newResults = data.results.map((item) =>
					item.id === note.id ? note : item,
				);
				return {
					...data,
					results: newResults,
				};
			},
		);
	}
}

export default QueryDataManager;
